/**
 * The notifications feed, assembled on the client from sources that already exist.
 *
 * Almost none of this is a notifications table. Every event here but one is read back out of
 * the data that recorded it in the first place — an application's stage history, the employer's
 * mark on it, a CV view, a follower row, a reply on your post, the agent's job suggestions,
 * an applicant on your ad — each of which already carries the moment it happened. So the
 * timestamps are the server's, not "when this device noticed", and nothing is lost while
 * the app is closed.
 *
 * The exception is `diil_notifications` (getStoredNotifications below), which exists because
 * one event had nothing recording it: an employer's job offer used to be written as a comment
 * on the candidate's newest Ажлын Групп post, so it could only reach someone who had already
 * posted. Everything else here still derives.
 *
 * Two shapes of source, handled differently:
 *
 *   - **Event rows** (stage history, CV views, followers, comments, applicants). These are
 *     already a log. Read them, map them, done.
 *   - **Current values** (an employer's mark, the suggested-job list on a post). A value is
 *     not an event, so the caller's stored snapshot says *which* ones moved, and then the
 *     status-history endpoint is asked *when* it moved. The snapshot never supplies a time.
 *
 * Every source is fetched with `allSettled`: one endpoint failing must cost its own rows and
 * nothing else. Nothing in here throws, and nothing here translates — an event carries a
 * `kind` and `params`, and the page turns those into text.
 */

import http from '../client';
import { ENDPOINTS } from '../config';
import { JOB_LINK_RE } from '../../utils/jobComment';
import { getAppliedList } from './candidateApply';
import { getViewedList } from './jobView';
import { getFollowers } from './companyFollow';
import { getGroupPosts, toFeedPost } from './groupPosts';
import { getJobDetail } from './jobs';
import { getJobApplicants } from './employerJobs';
import { changedKeys, newSuggestionIds } from '../../utils/notificationStore';
import { employerAdPath } from '../../utils/employerAdLink';

/** Older than this and it is history, not a notification. */
const MAX_AGE_DAYS = 30;

/** How many of the employer's ads are scanned for new applicants. */
const MAX_EMPLOYER_ADS = 20;

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

/**
 * Comments addressed to the caller — on a post of theirs, or answering a comment of theirs.
 * Each row carries a `reason` ("POST" / "REPLY") saying which.
 *
 * The feed cannot answer this: it previews only the three OLDEST replies per post and only
 * for the posts on the page being read, so a busy thread never shows its newest reply and an
 * older post shows none at all. This endpoint is scoped to the caller by their token.
 */
export const getMyPostComments = async (limit = 50) => {
  try {
    const response = await http.get(ENDPOINTS.GROUP_POSTS.MY_COMMENTS, { limit });
    const data = response.data?.data || response.data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Notifications that are a stored row rather than something derived.
 *
 * Only job offers today. Scoped to the caller by their token — there is deliberately no
 * candidateId parameter on this endpoint.
 */
export const getStoredNotifications = async (limit = 50) => {
  try {
    const response = await http.get(ENDPOINTS.NOTIFICATIONS.MINE, { limit });
    const data = response.data?.data || response.data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Likes on the caller's own posts. Each row names who liked it and when, and carries the
 * opening of the post so the row can say which one.
 *
 * The feed cannot answer this either: a post carries only its like *count*, which is neither
 * a person nor a moment, and an older post is nowhere near the first page it reads.
 */
export const getMyPostLikes = async (limit = 50) => {
  try {
    const response = await http.get(ENDPOINTS.GROUP_POSTS.MY_LIKES, { limit });
    const data = response.data?.data || response.data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * When one application's status became what it now is.
 *
 * `/candidate/applied-list` carries the current `complete_type` but not the moment it was
 * set; this audit trail carries both. Asked only for applications a snapshot says have
 * actually moved, so a quiet account costs nothing.
 */
export const getStatusHistory = async (assessmentPlayerId) => {
  try {
    const response = await http.get(`/assessmentPlayer/status-history/${assessmentPlayerId}`);
    const data = response.data?.data || response.data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const settled = (result) => (result.status === 'fulfilled' ? result.value : null);

const rowsOf = (result) => {
  const value = settled(result);
  if (!value || !value.success) return [];
  // Some of these services unwrap the server's { data: … } envelope and some hand back the
  // raw client result, so the list can sit one level deeper. SavedJobs.jsx reads them the
  // same defensive way.
  const data = value.data?.data || value.data;
  return Array.isArray(data) ? data : [];
};

const toIso = (value) => {
  if (!value) return null;
  // The server sends "yyyy-MM-dd HH:mm:ss" (Jackson pattern) as well as ISO strings;
  // Safari parses the former as NaN unless the space becomes a T.
  const date = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const withinWindow = (iso) => {
  if (!iso) return false;
  const age = Date.now() - new Date(iso).getTime();
  return age >= 0 && age <= MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
};

/**
 * One row of the page. `params` are the values the label needs; the page owns the wording,
 * so nothing here has to know which language is on.
 *
 * `approx` marks a time we could not source from the server (see statusEvents). The page
 * shows those as "саяхан" rather than inventing a precise one.
 */
const event = ({ id, kind, at, params = {}, photoUrl = null, to = null, approx = false }) => ({
  id, kind, at, params, photoUrl, to, approx,
});

// ---------------------------------------------------------------------------
// Candidate
// ---------------------------------------------------------------------------

/** Every stage the pipeline moved an application through. Timestamps ride along already. */
const stageEvents = (applied) =>
  applied.flatMap((item) =>
    (item.stageHistory || [])
      .map((stage) => {
        const at = toIso(stage.changedAt);
        if (!withinWindow(at)) return null;
        return event({
          id: `stage:${item.assessmentPlayerId}:${at}`,
          kind: 'stage',
          at,
          params: {
            job: item.assessmentName || '',
            company: item.companyName || '',
            stage: stage.stageNameMn || stage.stageName || '',
          },
          photoUrl: item.companyLogo || null,
          to: '/interviews',
        });
      })
      .filter(Boolean)
  );

/**
 * The employer's own mark ("Тэнцсэн", "Ярилцлага товолсон", …) changing.
 *
 * A mark is a column, not a log, so the snapshot picks out which applications moved and the
 * status-history endpoint says when. When that endpoint has no matching row — the AI chat
 * service moves some applications without writing one — the event is still raised, flagged
 * `approx` so it is shown without a time. Dropping it instead would lose the change for
 * good, because the snapshot is rewritten either way.
 */
const statusEvents = async (applied, statusSnapshot) => {
  const current = {};
  applied.forEach((item) => {
    if (item.assessmentPlayerId != null) current[String(item.assessmentPlayerId)] = item.status || null;
  });

  const moved = changedKeys(statusSnapshot, current);
  if (moved.length === 0) return { events: [], snapshot: current };

  const byId = new Map(applied.map((item) => [String(item.assessmentPlayerId), item]));
  const histories = await Promise.allSettled(moved.map((id) => getStatusHistory(id)));

  const events = moved.map((id, index) => {
    const item = byId.get(id);
    const rows = rowsOf(histories[index]);
    const match = rows.find((row) => row.newStatus === item.status) || rows[0] || null;
    const at = toIso(match?.createdDate);
    const params = {
      job: item.assessmentName || '',
      company: item.companyName || '',
      status: item.status || '',
    };
    return event({
      id: `status:${id}:${item.status}`,
      kind: 'status',
      at: at || new Date().toISOString(),
      approx: !at,
      params,
      photoUrl: item.companyLogo || null,
      to: '/interviews',
    });
  });

  return { events: events.filter((e) => e.approx || withinWindow(e.at)), snapshot: current };
};

const cvViewEvents = (views) =>
  views
    .map((row) => {
      const at = toIso(row.viewedAt);
      if (!withinWindow(at)) return null;
      return event({
        id: `cvview:${row.companyId}:${at}`,
        kind: 'cvView',
        at,
        params: { company: row.companyName || '' },
        photoUrl: row.companyLogo || null,
        to: row.companyId ? `/company/${row.companyId}` : null,
      });
    })
    .filter(Boolean);

/**
 * Who has opened the reader's profile — the same list the "Профайл үзсэн" sheet shows.
 *
 * Two kinds of viewer reach this, and they arrive in two shapes: getFollowers() leaves a
 * company row as the server sent it (companyId / companyName / companyPhoto) and rewrites a
 * person row through companyFollow.js's `toViewerRow` into { id, name, photo, to, kind:
 * 'person' }. Both are read here, because it is one sentence to the reader either way —
 * somebody looked at your profile — and reading only the company shape is what left a person
 * as a nameless, unclickable row.
 */
const followerEvents = (followers) =>
  followers
    .map((row) => {
      const at = toIso(row.createdDate);
      if (!withinWindow(at)) return null;
      const person = row.kind === 'person';
      // Namespaced by kind: company ids and candidate ids are separate counters, so one of
      // each sharing a number in the same second would otherwise collide into a single row —
      // and a collision here means one viewer's read mark silences the other's.
      const viewerId = person ? row.id : row.companyId;
      const to = person ? row.to : (row.companyId ? `/company/${row.companyId}` : null);
      return event({
        id: `follow:${person ? 'p' : 'c'}:${viewerId ?? 'x'}:${at}`,
        kind: 'follow',
        at,
        // `company` is the parameter's name, not a claim about who it is — the row reads
        // "{{company}} таны профайлыг үзлээ" for a person just as well.
        params: { company: (person ? row.name : row.companyName) || 'Хэн нэгэн' },
        photoUrl: (person ? row.photo : (row.companyPhoto || row.companyLogo)) || null,
        to: to || null,
      });
    })
    .filter(Boolean);

/**
 * Comments addressed to the reader — on a post of theirs, or answering a comment of theirs.
 *
 * The server says which via `reason`, because the two are different sentences and nothing
 * else in the row tells them apart. A reply used to be unreachable entirely: the lookup was
 * scoped to posts you own, so answering your comment under someone else's post notified
 * nobody.
 *
 * One shape of comment gets its own event kind: `buildJobComment` (utils/jobComment.js)
 * writes an employer's "Ажил санал болгох" offer as a comment whose text is company name,
 * title and a /job/{id} link — the only channel there is, since there is no notifications
 * table (see the file header). Read back with JOB_LINK_RE, it becomes `jobOffer` instead of
 * a plain comment: the reader is sent straight to the job, not into the thread it rode in
 * on, and the row is branded with the company that made the offer, not the person who
 * happened to click the button.
 */
const commentEvents = (comments) =>
  comments
    .map((row) => {
      const at = toIso(row.createdDate);
      if (!withinWindow(at)) return null;
      const isReply = row.reason === 'REPLY';

      const jobOffer = parseJobOfferComment(row.content);
      if (jobOffer) {
        return event({
          id: `comment:${row.id}`,
          kind: 'jobOffer',
          at,
          params: { company: jobOffer.company },
          to: `/job/${jobOffer.jobId}`,
        });
      }

      return event({
        id: `comment:${row.id}`,
        kind: isReply ? 'reply' : 'comment',
        at,
        params: { author: row.authorName || '', text: row.content || '' },
        photoUrl: row.authorPhotoUrl || null,
        // The comment id as well as the post: the feed scrolls to the comment itself, which
        // on a long thread is nowhere near the card.
        to: row.postId ? `/job-group?post=${row.postId}&comment=${row.id}` : '/job-group',
      });
    })
    .filter(Boolean);

/**
 * Unpacks a `buildJobComment` string back into `{ jobId, company }`, or null for an
 * ordinary comment. Only the company name (the string's first line) and the job id (the
 * trailing link) are needed here — the fuller parse CandidateSearchDemo does for the inline
 * job card is more than a notification row shows.
 */
const parseJobOfferComment = (content) => {
  const match = content?.match(JOB_LINK_RE);
  if (!match) return null;
  const company = content.split('\n')[0]?.trim();
  return company ? { jobId: match[2], company } : null;
};

/**
 * Job offers that are a real row — the ones that reach everybody.
 *
 * Same `jobOffer` kind the comment-derived offers produce, because it is the same sentence
 * to the reader; NotificationList already owns its icon, tone and wording. What is different
 * is who it can reach: a comment had to hang off a post of theirs, so this is the only path
 * to someone who has never posted in Ажлын Групп.
 *
 * Unknown `type` values are skipped rather than rendered: the table is general, so a build
 * older than a future notification kind must ignore it, not draw a blank row.
 *
 * The id is namespaced `stored:` rather than sharing `comment:`'s space — colliding ids
 * across the two sources would let one offer's read mark silence another.
 */
const storedEvents = (rows) =>
  rows
    .filter((row) => row.type === 'JOB_OFFER')
    .map((row) => {
      const at = toIso(row.createdDate);
      if (!withinWindow(at)) return null;
      // A row with no job is an offer to nowhere: the sentence names a company but the tap
      // would open /job/undefined. Skipped rather than drawn as a dead row.
      if (!row.jobId) return null;
      return event({
        id: `stored:${row.id}`,
        kind: 'jobOffer',
        at,
        params: { company: row.companyName || '' },
        // photoUrl is left for withCompanyPhotos. The row snapshots the company NAME (which
        // must not change after the fact) but deliberately not its logo, which is a current
        // fact about the company and would rot as a stored URL.
        to: `/job/${row.jobId}`,
      });
    })
    .filter(Boolean);

/**
 * Fills in the company logo on `jobOffer` rows, which neither source can set itself — the
 * comment text carries a name and a job id, and the stored row snapshots the name but
 * deliberately not the logo. One `getJobDetail` per distinct job (not per row: the same
 * offer can reach more than one candidate, and one offer can exist in both sources), in
 * parallel, and best-effort — a job that has since been taken down still shows its row,
 * just with the same briefcase fallback every other unphotographed kind gets.
 */
const withCompanyPhotos = async (events) => {
  const jobIds = [...new Set(
    events.filter((e) => e.kind === 'jobOffer' && e.to).map((e) => e.to.split('/').pop())
  )];
  if (jobIds.length === 0) return events;

  const results = await Promise.allSettled(jobIds.map((id) => getJobDetail(id)));
  const photoById = new Map(
    jobIds
      .map((id, i) => [id, settled(results[i])])
      .filter(([, res]) => res?.success && res.data)
      .map(([id, res]) => {
        // getJobDetail sometimes answers with the job wrapped in a one-element array
        // (see JobDetail.jsx's own unwrap) rather than the object itself.
        const job = Array.isArray(res.data) ? res.data[0] : res.data;
        return [id, job?.companyPhoto || job?.photoUrl || null];
      })
  );

  return events.map((e) => (
    e.kind === 'jobOffer' && e.to
      ? { ...e, photoUrl: photoById.get(e.to.split('/').pop()) || null }
      : e
  ));
};

/**
 * Likes other people put on the reader's posts.
 *
 * One row per like rather than "3 people liked your post": the server hands back who and
 * when, and a name is the part worth reading. Rows written before likes carried a time have
 * none, and `withinWindow` drops them rather than guessing one.
 */
const likeEvents = (likes) =>
  likes
    .map((row) => {
      const at = toIso(row.createdDate);
      if (!withinWindow(at)) return null;
      return event({
        id: `like:${row.id}`,
        kind: 'like',
        at,
        params: { author: row.likerName || '', text: row.postExcerpt || '' },
        photoUrl: row.likerPhotoUrl || null,
        to: row.postId ? `/job-group?post=${row.postId}` : '/job-group',
      });
    })
    .filter(Boolean);

/**
 * Jobs the matching agent added to one of your posts since last time.
 *
 * `suggestedAt` is the moment the agent answered, which is the honest time for "new jobs
 * arrived" — the ids themselves carry none.
 */
const suggestionEvents = (myPosts, suggestSnapshot) => {
  const current = {};
  myPosts.forEach((post) => {
    current[String(post.id)] = (post.suggestions?.jobs || []).map((job) => job.id).filter(Boolean);
  });

  const fresh = newSuggestionIds(suggestSnapshot, current);
  const byId = new Map(myPosts.map((post) => [String(post.id), post]));

  const events = Object.entries(fresh)
    .map(([postId, ids]) => {
      const post = byId.get(postId);
      const at = toIso(post?.suggestions?.suggestedAt) || toIso(post?.createdAt);
      if (!withinWindow(at)) return null;
      return event({
        id: `suggest:${postId}:${ids.join('-')}`,
        kind: 'suggest',
        at,
        params: { count: ids.length },
        to: `/job-group?post=${postId}`,
      });
    })
    .filter(Boolean);

  return { events, snapshot: current };
};

/**
 * Everything a jobseeker is told about.
 *
 * @param {{statusSnapshot: Object, suggestSnapshot: Object}} snapshots the stored ones
 * @returns {Promise<{events: Array, statusSnapshot: Object, suggestSnapshot: Object}>}
 */
export const buildCandidateEvents = async ({ statusSnapshot, suggestSnapshot } = {}) => {
  const [appliedRes, viewsRes, followersRes, commentsRes, likesRes, feedRes, storedRes] =
    await Promise.allSettled([
      getAppliedList(),
      getViewedList(),
      getFollowers(),
      getMyPostComments(),
      getMyPostLikes(),
      getGroupPosts({ page: 0, size: 30 }),
      getStoredNotifications(),
    ]);

  const applied = rowsOf(appliedRes);

  const feed = settled(feedRes);
  const feedRows = feed?.success
    ? (Array.isArray(feed.data) ? feed.data : feed.data?.content || [])
    : [];
  const myPosts = feedRows.map(toFeedPost).filter((post) => post.isMine);

  const status = await statusEvents(applied, statusSnapshot);
  const suggestions = suggestionEvents(myPosts, suggestSnapshot);
  // One photo pass over BOTH offer sources rather than one each: getJobDetail is deduped by
  // distinct job id inside, so an offer that exists as a stored row and as an older comment
  // costs one request, not two.
  const offers = await withCompanyPhotos([
    ...commentEvents(rowsOf(commentsRes)),
    ...storedEvents(rowsOf(storedRes)),
  ]);

  return {
    events: [
      ...stageEvents(applied),
      ...status.events,
      ...cvViewEvents(rowsOf(viewsRes)),
      ...followerEvents(rowsOf(followersRes)),
      ...offers,
      ...likeEvents(rowsOf(likesRes)),
      ...suggestions.events,
    ],
    statusSnapshot: status.snapshot,
    suggestSnapshot: suggestions.snapshot,
  };
};

// ---------------------------------------------------------------------------
// Employer
// ---------------------------------------------------------------------------

/**
 * What an employer is told about: someone applied to one of their ads, and an applicant's
 * state moving without them doing it (the AI interview finishing, a colleague marking
 * someone in the HR dashboard).
 *
 * One request per ad — the same `getJobApplicants` call Миний зар already caches, so opening
 * an ad afterwards is instant rather than a second fetch.
 *
 * @param {{posts: Array, applicantSnapshot: Object}} input the employer's own ads
 */
export const buildEmployerEvents = async ({ posts = [], applicantSnapshot } = {}) => {
  const ads = posts.filter((post) => post?.id).slice(0, MAX_EMPLOYER_ADS);
  if (ads.length === 0) return { events: [], applicantSnapshot: {} };

  const results = await Promise.allSettled(ads.map((ad) => getJobApplicants(ad.id)));

  /**
   * Where an employer notification lands: "Миний зарууд", with the ad named in the URL.
   *
   * Somebody applying is news about one ad, but it is read next to the others — the screen
   * an employer wants open is the one with their businesses across the top and that ad's
   * applicants in the pane. `/employer/my-posts?post=` opened the applicant table alone, one
   * ad deep, with no way back to the rest without leaving the page; `/company/:id` had the
   * opposite problem, arriving at the company with nothing in particular selected.
   *
   * The ad still goes in the URL when it carries no companyId — a few older ads do not, and
   * the page finds the business the ad is filed under itself.
   */
  const applicantTarget = (ad) => employerAdPath(ad?.companyId, ad?.id);

  const applied = [];
  const current = {};
  const byApplicant = new Map();

  results.forEach((result, index) => {
    const ad = ads[index];
    rowsOf(result).forEach((applicant) => {
      const at = toIso(applicant.appliedDate);
      current[String(applicant.id)] = applicant.status || null;
      byApplicant.set(String(applicant.id), { applicant, ad });

      if (withinWindow(at)) {
        applied.push(event({
          id: `applicant:${applicant.id}`,
          kind: 'applicant',
          at,
          params: { name: applicant.name || '', job: ad.title || ad.name || '' },
          photoUrl: applicant.photoUrl || null,
          to: applicantTarget(ad),
        }));
      }
    });
  });

  // Same "a column is not a log" problem as the candidate's own status, solved the same way.
  const moved = changedKeys(applicantSnapshot, current);
  const histories = await Promise.allSettled(moved.map((id) => getStatusHistory(id)));
  const statusChanges = moved
    .map((id, index) => {
      const entry = byApplicant.get(id);
      if (!entry) return null;
      const rows = rowsOf(histories[index]);
      const match = rows.find((row) => row.newStatus === entry.applicant.status) || rows[0] || null;
      const at = toIso(match?.createdDate);
      return event({
        id: `applicantStatus:${id}:${entry.applicant.status}`,
        kind: 'applicantStatus',
        at: at || new Date().toISOString(),
        approx: !at,
        params: {
          name: entry.applicant.name || '',
          job: entry.ad.title || entry.ad.name || '',
          status: entry.applicant.status || '',
        },
        photoUrl: entry.applicant.photoUrl || null,
        to: applicantTarget(entry.ad),
      });
    })
    .filter((e) => e && (e.approx || withinWindow(e.at)));

  return { events: [...applied, ...statusChanges], applicantSnapshot: current };
};

const notificationsService = {
  getMyPostComments,
  getMyPostLikes,
  getStatusHistory,
  buildCandidateEvents,
  buildEmployerEvents,
};

export default notificationsService;
